import { useState, useEffect, createContext, useContext } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useHistory } from 'react-router';

import {
  facebookLogout,
  initializeFacebook,
  loadFacebookSDK
} from 'utils/facebook';
import { CONNECT_PLUGIN_MODAL, META } from 'constants/connect-plugin';
import {
  disconnectChannel,
  sendChannelCreditinals,
  syncProducts
} from 'services/connectPlugin';
import { WHATSAPP } from 'constants/chats';
import { checkIfPopupIsBlocked } from 'utils';
import { sendSegment } from 'utils/segment';
import { ContextWrapper } from './wrapper.context';
import { useModal } from './modalProvider.context';

import SelectBusinessAndPageModal from 'components/connectPlugin/components/SelectBusinessAndPageModal/SelectBusinessAndPageModal';
import { notify } from 'components/Notify/Notify';
import { fmt } from 'components/IntlWrapper/IntlWrapper';
import { platformLocalize } from 'components/connectPlugin/utils/helper';

export const useFacebookIntegration = createContext({});

export const FacebookIntegrationProvider = ({ children }) => {
  const { formatMessage } = useIntl();
  const { businessInfo, isFetchingData, setBusinessInfo, updateBusinessInfo } =
    useContext(ContextWrapper);
  const { openModal } = useModal();
  const history = useHistory();

  const [businessAndPagesData, setBusinessesAndPagesData] = useState([]);
  const [isConnectionFailed, setIsConnectionFailed] = useState(false);
  const [selectedBusinessAndPagesData, setSelectedBusinessesAndPagesData] =
    useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSyncProducts = async ({ metaBusinessId, pageId, platform }) => {
    setIsLoading(true);
    try {
      await syncProducts({
        pageId,
        platform
      });

      notify({
        msg: fmt(
          {
            id: 'chats_plugins.install_plugins.connected_successfully'
          },
          {
            platform: platformLocalize(platform)
          }
        ),
        type: 'success'
      });

      sendSegment('SLLR_CHAT_GOT_CONNECTED', {
        CHANNEL: platform,
        EVENT_DATE: dayjs().format('YYYY-MM-DD'),
        BUSINESS_ID: businessInfo?._id
      });
      await updateBusinessInfo();
      history.push('/chats');
      localStorage.removeItem('currentPlugin');
      setIsConnectionFailed(false);
    } catch (error) {
      notify({ msg: error.message, error });
      setIsConnectionFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginToMeta = (configId, platform, setLoggedPlatform) => {
    const currentPlatform = CONNECT_PLUGIN_MODAL[platform?.toUpperCase()];
    if (checkIfPopupIsBlocked()) {
      notify({
        msg: 'You need to allow your pop-ups for this website.'
      });
    } else {
      try {
        return new Promise((resolve, reject) => {
          window.FB.login(
            function (FBresponse) {
              if (FBresponse.authResponse) {
                setIsLoading(true);
                const { userID, accessToken } = FBresponse.authResponse;
                if (userID && accessToken) {
                  sendChannelCreditinals({
                    platform,
                    userId: userID,
                    token: accessToken
                  })
                    .then(({ updated, businessesAndPages, message }) => {
                      setBusinessInfo((prev) => ({
                        ...prev,
                        featuresConfigurations: {
                          ...prev.featuresConfigurations,
                          plugins: {
                            ...prev.featuresConfigurations.plugins,
                            metaConfigurations: {
                              businessManagers: businessesAndPages
                            }
                          }
                        }
                      }));
                      setBusinessesAndPagesData(businessAndPagesData);
                      if (updated) {
                        setLoggedPlatform && setLoggedPlatform(platform);
                        if (platform === WHATSAPP) {
                          handleSyncProducts({
                            metaBusinessId:
                              businessesAndPages?.length &&
                              businessesAndPages[0].businessId,
                            platform
                          });
                        } else {
                          setIsLoading(false);
                          openModal(SelectBusinessAndPageModal, {
                            businessAndPagesData: businessesAndPages,
                            currentPlatform,
                            handleLoginToMeta,
                            cb: async ({ metaBusinessId, pageId }) => {
                              await handleSyncProducts({
                                metaBusinessId,
                                pageId,
                                platform
                              });
                            }
                          });
                        }
                        resolve(); // Resolve the promise here
                      } else {
                        notify({
                          msg: message
                        });
                        setIsLoading(false);
                        reject(new Error(message)); // Reject the promise with an error
                      }
                    })
                    .catch((error) => {
                      notify({ msg: error.message, error });
                      setIsLoading(false);
                      reject(error); // Reject the promise with an error
                    });
                }
              } else {
                notify({
                  msg: `${formatMessage({
                    id: 'chats_plugins.install_plugins.cancell_login'
                  })}`
                });
                reject(new Error('Login cancelled'));
              }
            },
            {
              config_id: configId, // configuration ID goes here
              response_type: 'code'
            }
          );
        });
      } catch (error) {
        notify({ msg: error.message, error });
        return Promise.reject(error); // Reject the promise with an error
      }
    }
  };

  const handleDisconnection = async ({ platform, msg }) => {
    try {
      setIsLoading(true);
      facebookLogout();
      const { deleted } = await disconnectChannel(platform?.toLowerCase());
      sendSegment('SLLR_CHAT_GOT_DISCONNECTED', {
        CHANNEL: platform,
        EVENT_DATE: dayjs().format('YYYY-MM-DD'),
        BUSINESS_ID: businessInfo?._id
      });
      if (deleted) {
        if (msg) {
          notify({ msg, type: 'success' });
        }
        await updateBusinessInfo();
        history.push('/appstore');
        localStorage.removeItem('currentPlugin');
      }
    } catch (error) {
      notify({ msg: error.message, error });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFacebookSDK();
    initializeFacebook();
  }, []);

  useEffect(() => {
    setBusinessesAndPagesData(
      businessInfo?.featuresConfigurations?.plugins?.metaConfigurations
        ?.businessManagers
    );
    setSelectedBusinessesAndPagesData(
      businessInfo?.featuresConfigurations?.plugins?.omniMessaging
    );
  }, [businessInfo]);

  const valuesToShare = {
    isLoading,
    handleSyncProducts,
    handleLoginToMeta,
    handleDisconnection,
    businessAndPagesData,
    isFetchingData,
    isConnectionFailed,
    setIsConnectionFailed,
    selectedBusinessAndPagesData
  };
  return (
    <useFacebookIntegration.Provider value={valuesToShare}>
      {children}
    </useFacebookIntegration.Provider>
  );
};

export default useFacebookIntegration;
